import React, { useState } from "react";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import usePagination from "src/customHooks/usePagination";
import { useTheme } from "src/context/theme.context";
import {
  FaCodeBranch,
  FaExclamationTriangle,
  FaSearch,
  FaUser
} from "react-icons/fa";
import { FunctionErrorsTable } from "../components/FunctionErrorsTable";
import { FunctionErrorsTableSkeleton } from "../components/FunctionErrorsTableSkeleton";
import {
  useCountFunctionErrorsQuery,
  useGetFunctionErrorsQuery
} from "../function-errors.service";
import {
  FunctionErrorsFilter,
  FunctionErrorRow
} from "../types/function-error.entity";

const FunctionErrorsPage = () => {
  const [userId, setUserId] = useState("");
  const [functionName, setFunctionName] = useState("");
  const [submitted, setSubmitted] = useState<FunctionErrorsFilter | null>(null);
  const { isDarkMode } = useTheme();

  const {
    data,
    numberOfPages,
    currentPage,
    next,
    prev,
    isSuccess,
    isError,
    isLoading,
    isFetching,
    updateQuery
  } = usePagination<FunctionErrorRow>({
    useGetDataQuery: useGetFunctionErrorsQuery,
    useCountData: useCountFunctionErrorsQuery,
    initQuery: {},
    skip: !submitted
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextQuery: FunctionErrorsFilter = {
      userId: userId.trim() || undefined,
      functionName: functionName.trim() || undefined
    };

    setSubmitted(nextQuery);
    updateQuery(nextQuery);
  };

  const cardBg = isDarkMode
    ? "bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-gray-700"
    : "bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-lg";
  const inputClass = isDarkMode
    ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-amber-500/50 focus:border-amber-500"
    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-amber-500 focus:border-amber-500";
  const labelClass = isDarkMode ? "text-gray-300" : "text-gray-700";
  const mutedClass = isDarkMode ? "text-gray-400" : "text-gray-600";

  return (
    <div className="page_container">
      <PageHeader text="Function Errors" />

      <div className={`rounded-2xl ${cardBg} p-6 mb-6`}>
        <div className="flex items-center gap-2 mb-4">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isDarkMode
                ? "bg-amber-500/20 text-amber-400"
                : "bg-amber-50 text-amber-600"
            }`}
          >
            <FaSearch className="w-5 h-5" />
          </div>
          <div>
            <h2
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Filter errors by user and function
            </h2>
            <p className={`text-sm ${mutedClass}`}>
              View aggregated cloud function errors filtered by user ID and
              function name. Leave all fields empty to list all errors.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[200px] flex-1 max-w-md">
              <label
                htmlFor="function-errors-user-id"
                className={`block text-sm font-medium mb-1.5 ${labelClass}`}
              >
                User ID
              </label>
              <div className="relative">
                <FaUser
                  className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`}
                />
                <input
                  id="function-errors-user-id"
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Leave empty to list all"
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${inputClass}`}
                />
              </div>
            </div>

            <div className="min-w-[200px] flex-1 max-w-md">
              <label
                htmlFor="function-errors-function-name"
                className={`block text-sm font-medium mb-1.5 ${labelClass}`}
              >
                Function name
              </label>
              <div className="relative">
                <FaCodeBranch
                  className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`}
                />
                <input
                  id="function-errors-function-name"
                  type="text"
                  value={functionName}
                  onChange={(e) => setFunctionName(e.target.value)}
                  placeholder="Exact deployed function name"
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${inputClass}`}
                />
              </div>
            </div>

            <button
              type="submit"
              className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 ${
                isDarkMode
                  ? "bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-900/30"
                  : "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md hover:shadow-lg"
              }`}
            >
              <FaSearch className="w-4 h-4" />
              Load errors
            </button>
          </div>

          {submitted?.functionName && (
            <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className={`text-xs ${mutedClass}`}>
                Function name: {submitted.functionName}
              </span>
            </div>
          )}
        </form>
      </div>

      {submitted ? (
        <>
          {isError ? (
            <div className={`rounded-2xl ${cardBg} p-8 text-center`}>
              <p className={mutedClass}>Failed to load function errors.</p>
            </div>
          ) : isSuccess ? (
            <>
              {isLoading || isFetching ? (
                <div
                  className={`overflow-x-auto mb-6 border border-gray-200 divide-y divide-gray-200 rounded-xl overflow-hidden ${isDarkMode ? "border-gray-700 divide-gray-700" : ""}`}
                >
                  <table className="min-w-[700px] w-full text-xs">
                    <thead className="bg-gray-900 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left uppercase tracking-wider">
                          Function
                        </th>
                        <th className="px-4 py-3 text-left uppercase tracking-wider">
                          Message
                        </th>
                        <th className="px-4 py-3 text-left uppercase tracking-wider">
                          Severity
                        </th>
                        <th className="px-4 py-3 text-center uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-4 py-3 text-center uppercase tracking-wider">
                          Count
                        </th>
                        <th className="px-4 py-3 text-left uppercase tracking-wider">
                          First / Last seen
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <FunctionErrorsTableSkeleton rows={8} />
                    </tbody>
                  </table>
                </div>
              ) : (
                <FunctionErrorsTable data={data} />
              )}
              <Pagination
                numberOfPages={numberOfPages}
                currentPage={currentPage}
                prev={prev}
                next={next}
                isLoading={isLoading || isFetching}
              />
            </>
          ) : null}
        </>
      ) : (
        <div className={`rounded-2xl border p-10 text-center ${cardBg}`}>
          <div
            className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
              isDarkMode
                ? "bg-amber-500/20 text-amber-400"
                : "bg-amber-50 text-amber-500"
            }`}
          >
            <FaExclamationTriangle className="w-8 h-8" />
          </div>
          <h3
            className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Ready to load errors
          </h3>
          <p className={`text-sm max-w-md mx-auto ${mutedClass}`}>
            Click "Load errors" to list all errors, or enter a user ID or
            function name to filter.
          </p>
        </div>
      )}
    </div>
  );
};

export default FunctionErrorsPage;
